import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { firmaApi, FirmarDocumentoData } from '../services/api.ts';

const FirmarDocumento: React.FC = () => {
  const [formData, setFormData] = useState<FirmarDocumentoData>({
    firmante: '',
    email: '',
    documento: '',
    titulo: '',
    descripcion: '',
    verificacionId: '',
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [showAdobeDialog, setShowAdobeDialog] = useState(false);
  const [adobeUrl, setAdobeUrl] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Por favor, selecciona un archivo PDF');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remover el prefijo "data:application/pdf;base64,"
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Por favor, selecciona un archivo PDF');
      return;
    }

    setLoading(true);
    setError('');
    setResultado(null);

    try {
      const base64Document = await convertFileToBase64(selectedFile);
      const submitData = {
        ...formData,
        documento: base64Document,
      };

      const response = await firmaApi.firmarDocumento(submitData);
      setResultado(response);
      
      // Si hay una URL de Adobe Sign, mostrar el diálogo
      if (response.url) {
        setAdobeUrl(response.url);
        setShowAdobeDialog(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al firmar documento');
    } finally {
      setLoading(false);
    }
  };

  const handleAdobeRedirect = () => {
    window.open(adobeUrl, '_blank', 'width=1000,height=700');
    setShowAdobeDialog(false);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Firmar Documento
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Sube tu documento PDF y será enviado a Adobe Sign para la firma electrónica.
          El proceso es seguro y legalmente vinculante.
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del firmante"
                name="firmante"
                value={formData.firmante}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email del firmante"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título del documento"
                name="titulo"
                value={formData.titulo}
                onChange={handleInputChange}
                placeholder="Ej: Contrato de Servicios"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción del documento"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="Breve descripción del documento a firmar"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ID de verificación (opcional)"
                name="verificacionId"
                value={formData.verificacionId}
                onChange={handleInputChange}
                placeholder="ID de verificación de identidad previa"
                helperText="Si ya completaste la verificación de identidad, incluye el ID aquí"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Documento PDF *
              </Typography>
              <input
                accept=".pdf"
                style={{ display: 'none' }}
                id="pdf-file-input"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="pdf-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Seleccionar archivo PDF
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" color="text.secondary">
                  Archivo seleccionado: {selectedFile.name}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !selectedFile}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Enviar para Firma'}
              </Button>
            </Grid>
          </Grid>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {resultado && !showAdobeDialog && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="h6">Documento enviado para firma</Typography>
            <Typography>ID: {resultado.id}</Typography>
            <Typography>Estado: {resultado.estado}</Typography>
            <Typography>Firmante: {resultado.firmante}</Typography>
            <Typography>Timestamp: {resultado.timestamp}</Typography>
          </Alert>
        )}
      </Paper>

      {/* Diálogo de Adobe Sign */}
      <Dialog open={showAdobeDialog} onClose={() => setShowAdobeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Firma Electrónica con Adobe Sign</DialogTitle>
        <DialogContent>
          <Typography>
            Tu documento ha sido enviado a Adobe Sign para la firma electrónica.
            Serás redirigido a la plataforma de Adobe Sign donde podrás:
          </Typography>
          <ul>
            <li>Revisar el documento completo</li>
            <li>Agregar tu firma electrónica</li>
            <li>Confirmar tu identidad</li>
            <li>Completar el proceso de firma</li>
          </ul>
          <Typography variant="body2" color="text.secondary">
            La firma electrónica tiene la misma validez legal que una firma manuscrita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAdobeDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={handleAdobeRedirect} variant="contained">
            Ir a Adobe Sign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FirmarDocumento; 